// Cloud-source connections (Dropbox / Google Drive). Two kinds:
//  - personal: a user's own link, reusable across every workspace they're in;
//  - workspace: linked by a workspace owner/admin (`scope=workspace` + the tenant hostname),
//    usable by everyone who uploads there — limited to one shared folder the admin picks. Talks to the auth API: connect / list / disconnect, browse a
// connected account's folders, and have the SERVER copy a file from it straight into an
// upload target minted by the content API (so the browser — often a phone — never
// downloads or uploads the photo itself).
import { authFetch } from "@/lib/cognito";
import { AUTH_API_ENDPOINT, tenantHostname } from "@/lib/apiDomain";
import type { TenantScope } from "@/lib/api/galleries";

export type ConnectionProvider = "dropbox" | "gdrive";

export interface CloudConnection {
  provider: ConnectionProvider;
  displayName: string | null;
  providerAccountId: string | null;
  scopes: string | null;
  status: string;
  createdAt: number | null;
  updatedAt: number | null;
  lastUsedAt: number | null;
  /** Workspace connections: who linked it, and the one folder members may use. */
  connectedBy?: { name?: string | null } | null;
  rootFolder?: { id: string; name: string; path?: string | null } | null;
}

/** Which connection a call is about: omitted = the signed-in user's own; `workspace` = that
 *  workspace's shared one. */
export type CloudOwner = { workspace?: TenantScope | null };

const ownerParams = (owner?: CloudOwner): URLSearchParams => {
  const qs = new URLSearchParams();
  const host = owner?.workspace ? tenantHostname(owner.workspace) : undefined;
  if (host) {
    qs.set("scope", "workspace");
    qs.set("hostname", host);
  }
  return qs;
};
const withQs = (path: string, qs: URLSearchParams) => {
  const q = qs.toString();
  return `${AUTH_API_ENDPOINT}${path}${q ? `?${q}` : ""}`;
};

export const PROVIDER_LABELS: Record<ConnectionProvider, string> = {
  dropbox: "Dropbox",
  gdrive: "Google Drive",
};

// Authenticated JSON call; a 401 refreshes the session once and retries (authFetch).
const api = (url: string, init: RequestInit = {}): Promise<Response> =>
  authFetch(url, { ...init, headers: { "Content-Type": "application/json" } });

export const listConnections = async (): Promise<CloudConnection[]> => {
  const res = await api(`${AUTH_API_ENDPOINT}/connections`);
  if (!res.ok) throw new Error(`Couldn't load connections (${res.status}).`);
  const data = await res.json();
  return (data?.connections || []) as CloudConnection[];
};

/** A workspace's shared connections, and whether the caller (owner/admin) may manage them. */
export const listWorkspaceConnections = async (
  workspace: TenantScope,
): Promise<{ connections: CloudConnection[]; canManage: boolean }> => {
  const res = await api(withQs("/connections", ownerParams({ workspace })));
  // Not a member / an older backend without workspace connections → simply none.
  if (res.status === 403 || res.status === 404) return { connections: [], canManage: false };
  if (!res.ok) throw new Error(`Couldn't load the workspace's connections (${res.status}).`);
  const data = await res.json();
  return { connections: (data?.connections || []) as CloudConnection[], canManage: !!data?.canManage };
};

/** Choose (or clear, with null) the one folder workspace members may copy from. */
export const setWorkspaceFolder = async (
  provider: ConnectionProvider,
  workspace: TenantScope,
  folderId: string | null,
): Promise<CloudConnection> => {
  const res = await api(withQs(`/connections/${provider}/settings`, ownerParams({ workspace })), {
    method: "POST",
    body: JSON.stringify({ rootFolder: folderId == null ? null : { id: folderId } }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.message || `Couldn't save the shared folder (${res.status}).`);
  }
  const data = await res.json();
  return (data?.connection ?? data) as CloudConnection;
};

/** Get the provider's authorization URL to send the browser to. `returnTo` (a photohawk page)
 *  is where the provider sends the user back to afterwards, with `?connected=<provider>` or
 *  `?error=…` added; without it they land on the account's Connections page. */
export const startConnection = async (provider: ConnectionProvider, returnTo?: string, owner?: CloudOwner): Promise<string> => {
  const qs = ownerParams(owner);
  if (returnTo) qs.set("return", returnTo);
  const res = await api(withQs(`/connections/${provider}/start`, qs));
  if (res.status === 403) throw new Error("Only workspace owners and administrators can connect an account for the workspace.");
  if (res.status === 501) throw new Error(`${PROVIDER_LABELS[provider]} isn't available yet.`);
  if (!res.ok) throw new Error(`Couldn't start the connection (${res.status}).`);
  const data = await res.json();
  if (!data?.url) throw new Error("No authorization link was returned.");
  return data.url as string;
};

export const removeConnection = async (provider: ConnectionProvider, owner?: CloudOwner): Promise<void> => {
  const res = await api(withQs(`/connections/${provider}`, ownerParams(owner)), {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`Couldn't disconnect (${res.status}).`);
};

// --- Browsing + transfer ----------------------------------------------------------

export interface CloudEntry {
  id: string;
  name: string;
  kind: "folder" | "file";
  size?: number | null;
  modified?: string | null;
  mimeType?: string | null;
  /** Can be imported: a JPEG under the size limit (other formats need the browser's converter). */
  importable?: boolean;
}

export interface CloudFolder {
  /** `isRoot`: a workspace connection's shared folder — as high as members can go. */
  folder: { id: string; name: string; path?: string | null; isRoot?: boolean };
  entries: CloudEntry[];
  cursor: string | null;
}

/** The connection needs signing in again (revoked / expired) — offer "Reconnect". */
export class CloudReconnectError extends Error {
  provider: ConnectionProvider;
  constructor(provider: ConnectionProvider, message?: string) {
    super(message || `Reconnect ${PROVIDER_LABELS[provider]} to carry on.`);
    this.name = "CloudReconnectError";
    this.provider = provider;
  }
}

/** A workspace connection with no shared folder chosen yet (members can't use it until then). */
export class CloudNeedsFolderError extends Error {
  constructor(message?: string) {
    super(message || "An admin hasn't chosen a shared folder for this workspace yet.");
    this.name = "CloudNeedsFolderError";
  }
}

const failure = async (provider: ConnectionProvider, res: Response, what: string): Promise<Error> => {
  const body = await res.json().catch(() => ({}) as Record<string, unknown>);
  if (body?.reconnect) return new CloudReconnectError(provider, body?.message as string | undefined);
  if (body?.needsFolder) return new CloudNeedsFolderError(body?.message as string | undefined);
  return new Error((body?.message as string) || `${what} (${res.status}).`);
};

/** One page of a folder in the connected account ("" = the top level). */
export const browseCloud = async (
  provider: ConnectionProvider,
  folder = "",
  cursor?: string | null,
  signal?: AbortSignal,
  owner?: CloudOwner & { pick?: boolean },
): Promise<CloudFolder> => {
  const qs = ownerParams(owner);
  if (folder) qs.set("folder", folder);
  if (cursor) qs.set("cursor", cursor);
  // Admins choosing the shared folder browse the whole account.
  if (owner?.pick) qs.set("pick", "1");
  const res = await api(withQs(`/connections/${provider}/browse`, qs), {
    signal,
  });
  if (!res.ok) throw await failure(provider, res, `Couldn't open that ${PROVIDER_LABELS[provider]} folder`);
  return res.json();
};

/** Every entry of a folder (follows the cursor). */
export const browseCloudAll = async (
  provider: ConnectionProvider,
  folder = "",
  signal?: AbortSignal,
  owner?: CloudOwner & { pick?: boolean },
): Promise<CloudFolder> => {
  const first = await browseCloud(provider, folder, null, signal, owner);
  const entries = [...first.entries];
  let cursor = first.cursor;
  for (let guard = 0; cursor && guard < 200; guard += 1) {
    const next = await browseCloud(provider, folder, cursor, signal, owner);
    entries.push(...next.entries);
    cursor = next.cursor;
  }
  return { ...first, entries, cursor: null };
};

/** An upload target from the content mint — a presigned R2 PUT or S3 POST form. */
export type TransferTarget =
  | { put: { url: string; headers?: Record<string, string> } }
  | { fields: Record<string, string>; directUrl?: string; url?: string };

/** Have the server copy one file from the connected account into `target`. */
export const transferFromCloud = async (
  provider: ConnectionProvider,
  fileId: string,
  target: TransferTarget,
  signal?: AbortSignal,
  owner?: CloudOwner,
): Promise<{ bytes?: number }> => {
  const res = await api(withQs(`/connections/${provider}/transfer`, ownerParams(owner)), {
    method: "POST",
    body: JSON.stringify({ fileId, target }),
    signal,
  });
  if (!res.ok) {
    const err = await failure(provider, res, "Copy failed");
    // Keep the status visible to the uploader's retry logic ("(401)", "(502)"…).
    if (!(err instanceof CloudReconnectError)) err.message = `${err.message.replace(/\s*\(\d{3}\)\.?$/, "")} (${res.status})`;
    throw err;
  }
  return res.json().catch(() => ({}));
};

// --- Cloud files in the upload queue ------------------------------------------------
// The uploader's intake + summary work on File objects. A cloud file becomes a stand-in File
// (right name, type and size, no bytes) registered here with where it really lives, so the
// same duplicate check / summary / queue handle it; the upload manager copies it server-side.
export interface CloudSource {
  provider: ConnectionProvider;
  id: string;
  /** Set when it comes from the workspace's shared connection (not the user's own). */
  workspace?: TenantScope;
}
const cloudSources = new WeakMap<File, CloudSource>();

export const cloudFile = (provider: ConnectionProvider, entry: CloudEntry, relPath?: string, owner?: CloudOwner): File => {
  const f = new File([], entry.name, { type: "image/jpeg", lastModified: entry.modified ? Date.parse(entry.modified) || Date.now() : Date.now() });
  Object.defineProperty(f, "size", { value: Number(entry.size) || 0 });
  if (relPath) Object.defineProperty(f, "webkitRelativePath", { value: relPath });
  cloudSources.set(f, { provider, id: entry.id, ...(owner?.workspace ? { workspace: owner.workspace } : {}) });
  return f;
};

export const cloudSourceOf = (f: File) => cloudSources.get(f);

